import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	IconButton,
	Alert,
	Tooltip,
} from '@mui/material';
import { ItemWithId } from 'components/HMRC/data/types';
import { COLORS } from 'services/constants/colors';
import EditIcon from '@mui/icons-material/Edit';
import { EasTableValue } from 'ui/EasTable/EasTableRow/EasTableValue';
import { useDispatch } from 'react-redux';
import { TEasParamsBoxParams } from './types';
import { EditParamsDialog } from './EditParamsDialog';
import { EasCard } from '../../../../ui/EasCard';
import { EasSkeleton } from '../../../../ui/EasSkeleton';
import { Close, HelpOutline } from '@mui/icons-material';
import { EasTooltip } from '../../../../ui/EasTooltip';

export const EasParamsBox = <T extends ItemWithId>({
	link,
	items,
	boxStructure,
	columnNumber = 1,
	editable = false,
	fontSize = 'xl',
	loading = false,
	editDialog = null,
	banner,
	minHeight = 'auto',
	showCloseButton = false,
	onHide,
}: TEasParamsBoxParams<T>) => {
	const EditDialogComponent = editDialog;
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const tableContainerRef = useRef<HTMLDivElement | null>(null);
	const [nameColWidth, setNameColWidth] = useState<number>(0);

	// console.log('=====EasParamsBox items=====', items);

	const handleEditClick = () => {
		setIsEditDialogOpen(true);
	};

	const handleEditClose = () => {
		setIsEditDialogOpen(false);
	};

	const bodyArray = boxStructure.body.filter((item) => item.show !== false);

	const longestLabel = useMemo(() => {
		return bodyArray.reduce((acc, curr) => {
			const label = `${curr.name}:`;
			return label.length > acc.length ? label : acc;
		}, '');
	}, [bodyArray]);

	useEffect(() => {
		const measure = () => {
			const containerWidth = tableContainerRef.current?.clientWidth ?? 0;
			if (!containerWidth) return;
			const context = document.createElement('canvas').getContext('2d');
			if (!context) return;
			const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize || '16');
			const fontSizePx = 0.75 * rootFontSize; // Typography sx fontSize: 0.75rem
			const fontFamily =
				getComputedStyle(document.body).fontFamily || 'Roboto, Helvetica, Arial, sans-serif';
			context.font = `${fontSizePx}px ${fontFamily}`;
			const textWidth = context.measureText(longestLabel).width;
			const padding = 16; // left+right padding in the label cell
			const desired = Math.ceil(textWidth + padding);
			const maxAllowed = Math.floor(containerWidth * 0.5);
			setNameColWidth(Math.min(desired, maxAllowed));
		};

		// initial measure
		measure();

		// re-measure when fonts are ready (if supported)
		const fontsReady = (document as unknown as { fonts?: { ready?: Promise<void> } })?.fonts?.ready;
		if (fontsReady && typeof fontsReady.then === 'function') {
			fontsReady.then(() => measure()).catch(() => {});
		}

		const onResize = () => measure();
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, [longestLabel]);

	return (
		<>
			<EasCard
				id={`em-params-box-${boxStructure.title}`}
				title={boxStructure.title}
				link={link}
				action={
					<Box sx={{ p: 0, m: 0 }}>
						{editable && (
							<Tooltip title="Edit">
								<IconButton onClick={handleEditClick} size="small" sx={{ p: 0, m: 0 }}>
									<EditIcon sx={{ color: 'primary.main', fontSize: '1.5rem', p: 0, m: 0 }} />
								</IconButton>
							</Tooltip>
						)}
						{showCloseButton && (
							<EasTooltip title="Hide">
								<IconButton onClick={onHide} size="small" sx={{ p: 0, m: 0 }}>
									<Close sx={{ color: 'primary.main', fontSize: '1.5rem', p: 0, m: 0 }} />
								</IconButton>
							</EasTooltip>
						)}
					</Box>
				}
				loading={loading}
			>
				<Box sx={{ padding: '0px!important', height: '100%', width: '100%' }}>
					{banner && (
						<Alert severity="warning" sx={{ color: 'warning.dark', mb: 1 }}>
							{banner}
						</Alert>
					)}
					{bodyArray.length > 0 &&
						items !== null &&
						Object.keys(items).length > 0 &&
						boxStructure !== null && (
							<TableContainer ref={tableContainerRef} sx={{ padding: '0px', paddingBottom: '0px' }}>
								<Table size="small" sx={{ tableLayout: 'fixed' }}>
									<TableBody>
										{Array.from({ length: Math.ceil(bodyArray.length / columnNumber) }).map(
											(_, rowIndex) => {
												const rowItems = bodyArray.slice(
													rowIndex * columnNumber,
													(rowIndex + 1) * columnNumber,
												);
												return (
													<TableRow key={`row-${rowItems[0]?.name || rowIndex}`}>
														{rowItems.map((item, colIndex) => {
															return (
																<React.Fragment key={String(item.selector)}>
																	<TableCell
																		sx={{
																			width: nameColWidth ? `${nameColWidth}px` : undefined,
																			padding: '6px 8px',
																			border: 'none',
																			verticalAlign: 'top',
																		}}
																	>
																		<Typography
																			variant="body2"
																			sx={{ fontSize: '0.75rem' }}
																			color="primary"
																		>
																			{item.name}:
																		</Typography>
																	</TableCell>
																	<TableCell
																		sx={{
																			padding: '6px 8px',
																			border: 'none',
																			verticalAlign: 'center',
																			borderRight:
																				colIndex === rowItems.length - 1
																					? 'none'
																					: `1px solid ${COLORS.SECONDARY}`,
																		}}
																	>
																		{items[item.selector] === null ? (
																			'–'
																		) : (
																			<Box
																				sx={{
																					display: 'flex',
																					alignItems: 'center',
																					gap: '4px',
																					position: 'relative',
																				}}
																			>
																				<>
																					<EasTableValue
																						value={
																							item.value
																								? item.value(items as T)
																								: (items[item.selector] as string)
																						}
																						type={item.type}
																						tableStructureItem={item}
																						item={items[item.selector]}
																						size={fontSize}
																						bold
																					/>
																					{item.description && (
																						<EasTooltip title={item.description} arrow>
																							<HelpOutline
																								sx={{ color: 'primary.main', fontSize: '16px' }}
																							/>
																						</EasTooltip>
																					)}
																				</>
																			</Box>
																		)}
																	</TableCell>
																</React.Fragment>
															);
														})}
													</TableRow>
												);
											},
										)}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					{(bodyArray.length === 0 ||
						items === null ||
						Object.keys(items).length === 0 ||
						boxStructure === null) &&
						!loading && (
							<Box sx={{ padding: '0px', paddingBottom: '0px' }}>
								<Typography
									variant="body2"
									sx={{ fontSize: '24px', textAlign: 'center', fontWeight: 'bold' }}
									color={COLORS.SECONDARY}
								>
									No data found...
								</Typography>
							</Box>
						)}
					{loading && (!items || Object.keys(items).length === 0 || !boxStructure) && (
						<Box sx={{ padding: '0px', paddingBottom: '0px', width: '100%' }}>
							{Array.from({ length: Math.ceil(bodyArray.length / columnNumber / 2) }).map(
								(_, index) => (
									<EasSkeleton key={index} />
								),
							)}
						</Box>
					)}
				</Box>
			</EasCard>
			{EditDialogComponent ? (
				<EditDialogComponent open={isEditDialogOpen} onClose={handleEditClose} />
			) : (
				<EditParamsDialog<T>
					open={isEditDialogOpen}
					setClose={handleEditClose}
					data={items}
					boxStructure={boxStructure}
					columnNumber={columnNumber}
					// onSave={handleSave}
				/>
			)}
		</>
	);
};
